// ============================================================
// ELO ENGINE — rating calculation
//
// Standard ELO: new = old + K × (actual - expected)
//   actual = 1 (won), 0 (lost), 0.5 (draw)
// + MOV (margin of victory) gives up to +50% bonus for crushing wins
// + NEWBIE multiplier gives 2x for newbie/inactive players
//
// All thresholds/factors are read from EngineSettings (mutable singleton)
// so that changes to settings take effect immediately.
// ============================================================

import { EngineSettings, type EngineSettingsValues } from './constants.js';
import type { Player } from './types.js';

/**
 * Computes the K multiplier for a player based on experience and activity.
 *
 * MODEL (grace + linear decay):
 *   Match 1 to grace:           maxBoost (flat)
 *   Match grace+1 to threshold: decays linearly towards 1.0x
 *   After threshold:            1.0x
 *
 * With the default values (grace=5, threshold=20, max=2.0x):
 *   Match 1-5:  2.0x (full boost)
 *   Match 10:   1.67x
 *   Match 15:   1.33x
 *   Match 20:   1.0x (established)
 *
 * Formula:
 *   if matches < grace:     boost = max
 *   else:                   boost = max - (max-1) × (matches - grace) / (threshold - grace)
 *
 * Inactivity: players who have been inactive for longer than NEWBIE_INACTIVE_DAYS
 * get the full maxBoost — regardless of how many matches they had played before.
 *
 * kMultiplier adjustment: if a match has a lower ELO weight (e.g. Americano
 * at 50%), ELO moves more slowly — therefore BOTH grace and threshold are scaled
 * proportionally. effectiveGrace = grace / kMul, effectiveThreshold = threshold / kMul.
 *   - Padel24 (kMul=1.0): 5 matches full boost, then decay until match 20
 *   - Americano (kMul=0.5): 10 matches full boost, then decay until match 40
 *   - Club championship (kMul=1.5): ~3 matches full boost, decay until ~13
 *
 * @param {Object} player - player object with matchesPlayed and lastMatchAt
 * @param {number} now - current timestamp (Date.now())
 * @param {number} kMultiplier - tournament ELO weight (1.0 default)
 * @returns {number} K multiplier between 1.0 and NEWBIE_K_MULTIPLIER
 */
/**
 * `settings` defaults to the live global singleton, so an omitted argument
 * reads exactly what this function read before #243 — including a value the
 * browser applied at runtime. The server passes an explicit frozen snapshot
 * instead, so one operation cannot straddle a settings change.
 */
export function getPlayerKMultiplier(
  player: Player | null | undefined,
  now: number = Date.now(),
  kMultiplier: number = 1.0,
  settings: EngineSettingsValues = EngineSettings,
): number {
  if (!player) return 1.0;
  const matches = player.matchesPlayed || 0;
  const threshold = settings.NEWBIE_MATCH_THRESHOLD;
  const grace = settings.NEWBIE_GRACE_MATCHES;
  const maxBoost = settings.NEWBIE_K_MULTIPLIER;

  // If the new player system is turned off (threshold = 0), return 1.0
  if (threshold === 0) return 1.0;

  // Check inactivity — full boost for returning players
  const lastMatch = player.lastMatchAt;
  if (lastMatch) {
    const daysSince = (now - lastMatch) / (86400 * 1000);
    if (daysSince > settings.NEWBIE_INACTIVE_DAYS) return maxBoost;
  }

  // Scale both grace and threshold by kMultiplier — americano (0.5x) needs
  // 2x as many matches to converge, club championship (1.5x) needs fewer.
  const safeKMul = kMultiplier > 0 ? kMultiplier : 1.0;
  const effectiveThreshold = threshold / safeKMul;
  const effectiveGrace = grace / safeKMul;

  // Established: no boost
  if (matches >= effectiveThreshold) return 1.0;

  // Within grace period: full boost
  if (matches < effectiveGrace) return maxBoost;

  // Between grace and threshold: linear decay.
  // Edge case: if grace == threshold (or grace >= threshold), avoid
  // division by zero — return maxBoost since we are still within range.
  const declineRange = effectiveThreshold - effectiveGrace;
  if (declineRange <= 0) return maxBoost;
  const progress = (matches - effectiveGrace) / declineRange; // 0.0 → 1.0
  return maxBoost - (maxBoost - 1.0) * progress;
}

/**
 * Formats a boost value as compact text.
 * Whole numbers: "2x", "3x"  (no unnecessary decimals)
 * With decimals: "1.5x", "1.2x" (one decimal)
 * Used everywhere the boost multiplier is shown in the UI to save space.
 */
export function formatBoost(mul: number | null | undefined): string {
  if (mul == null || isNaN(mul)) return '1x';
  const rounded = Math.round(mul * 10) / 10;
  // Whole number (1.0, 2.0, 3.0) → show without decimal
  if (rounded === Math.floor(rounded)) return `${Math.floor(rounded)}x`;
  // Otherwise one decimal
  return `${rounded.toFixed(1)}x`;
}

/**
 * Computes the status of a player — whether they are "new", "returning", or established.
 * Used in the UI to show a badge on the player profile.
 *
 * @returns {Object} { isNew, isReturning, matchesPlayed, matchesUntilEstablished }
 */
export function getPlayerStatus(
  player: Player | null | undefined,
  now: number = Date.now(),
  kMultiplier: number = 1.0,
) {
  const matches = player?.matchesPlayed || 0;
  const threshold = EngineSettings.NEWBIE_MATCH_THRESHOLD;
  const safeKMul = kMultiplier > 0 ? kMultiplier : 1.0;
  const effectiveThreshold = threshold / safeKMul;
  const isNew = threshold > 0 && matches < effectiveThreshold;

  let isReturning = false;
  if (!isNew && player?.lastMatchAt && threshold > 0) {
    const daysSince = (now - player.lastMatchAt) / (86400 * 1000);
    isReturning = daysSince > EngineSettings.NEWBIE_INACTIVE_DAYS;
  }

  // Compute current boost (same formula as getPlayerKMultiplier)
  const currentBoost = getPlayerKMultiplier(player, now, kMultiplier);

  return {
    isNew,
    isReturning,
    isBoosted: currentBoost > 1.0,
    currentBoost, // current multiplier (1.0-2.0)
    matchesPlayed: matches,
    matchesUntilEstablished: Math.max(0, Math.ceil(effectiveThreshold - matches)),
    effectiveThreshold, // number of matches needed in this tournament
  };
}

// Compute margin-of-victory multiplier — for TOURNAMENT MATCHES
// Used in Padel24 tournaments where each match has a single number (score1, score2)
// e.g. 9-7, 9-5, 6-4
//
// Formula: 1 + 0.5 × (margin / winnerScore) — gives 1.0 to 1.5
// Examples:
//   9-9 (draw): 1.0
//   9-5: 1.22 (slightly above standard)
//   9-0 (crushing): 1.5 (max bonus)
/**
 * `settings` defaults to the live global singleton, so an omitted argument
 * reads exactly what this function read before #243 — including a value the
 * browser applied at runtime. The server passes an explicit frozen snapshot
 * instead, so one operation cannot straddle a settings change.
 */
export function calcMovMultiplier(
  winnerScore: number | null | undefined,
  loserScore: number | null | undefined,
  settings: EngineSettingsValues = EngineSettings,
): number {
  if (winnerScore == null || loserScore == null) return 1;
  const winScore = Math.max(winnerScore, loserScore);
  const lossScore = Math.min(winnerScore, loserScore);
  if (winScore <= 0) return 1;
  const margin = (winScore - lossScore) / winScore; // 0 to 1
  return 1 + settings.MOV_MAX_BONUS * margin; // 1.0 to 1 + max_bonus
}

// Compute margin-of-victory multiplier — for SINGLE MATCHES (best of 3 padel)
// Used when we have the sum of games over multiple sets (e.g. 12-4, 16-14)
//
// Based on Weighted Elo (Angelini et al., 2022) — weights ELO based on
// the proportion of games won, not just the set result.
//
// Examples:
//   6-0, 6-0 (12-0):  100% of games → MOV 2.0 (total dominance, double effect)
//   6-2, 6-2 (12-4):  75% of games  → MOV 1.5 (clear win)
//   6-3, 6-3 (12-6):  67% of games  → MOV 1.33 (good win)
//   6-4, 6-4 (12-8):  60% of games  → MOV 1.2 (solid)
//   7-5, 7-5 (14-10): 58% of games  → MOV 1.17 (even 2-0)
//   7-6, 7-6 (14-12): 54% of games  → MOV 1.08 (very even)
//   6-4, 4-6, 6-4 (16-14): 53% of games → MOV 1.07 (narrow 3-setter)
export function calcSingleMatchMov(
  winnerGames: number | null | undefined,
  loserGames: number | null | undefined,
): number {
  if (winnerGames == null || loserGames == null) return 1;
  const winScore = Math.max(winnerGames, loserGames);
  const total = winnerGames + loserGames;
  if (total <= 0) return 1;
  const winnerProp = winScore / total; // 0.5 to 1.0 for the winner
  return 1 + 2 * (winnerProp - 0.5);
}

// MOV ("big win") for CLUB CHAMPIONSHIP — based on games (like Padel24), not
// just sets. A super-tiebreak set (points to 10) counts as 1-0, so that an
// even 3-setter decided 10-0 in the super-tiebreak does NOT become a "big win"
// (the match was close — it went to a deciding set). 6-0 6-0 gives full effect.
export function kmMatchMov(score: any, settings: EngineSettingsValues = EngineSettings): number {
  if (!score || !Array.isArray(score.sets) || score.sets.length === 0) {
    return calcMovMultiplier(score?.t1, score?.t2, settings);
  }
  let t1g = 0,
    t2g = 0;
  score.sets.forEach((s: any) => {
    const hi = Math.max(s.t1 || 0, s.t2 || 0);
    if (hi >= 8) {
      // super-tiebreak set → counts as 1-0
      if ((s.t1 || 0) > (s.t2 || 0)) t1g += 1;
      else t2g += 1;
    } else {
      t1g += s.t1 || 0;
      t2g += s.t2 || 0;
    }
  });
  const winnerT1 = (score.t1 ?? 0) > (score.t2 ?? 0);
  return calcSingleMatchMov(winnerT1 ? t1g : t2g, winnerT1 ? t2g : t1g);
}

/**
 * `settings` defaults to the live global singleton, so an omitted argument
 * reads exactly what this function read before #243 — including a value the
 * browser applied at runtime. The server passes an explicit frozen snapshot
 * instead, so one operation cannot straddle a settings change.
 */
export function calcEloChange(
  teamElo: number,
  opponentElo: number,
  result: string,
  mov: number = 1,
  kMultiplier: number = 1,
  newbieMul: number = 1,
  settings: EngineSettingsValues = EngineSettings,
): number {
  // result: 'win' (1), 'loss' (0), 'draw' (0.5)
  // kMultiplier: ELO weight (americano = 0.5, padel24 = 1.0, single match = optional 0-2.0, club championship = 1.75, league play = 2.0)
  // newbieMul: extra K boost for newbie/inactive players (1.0 normally, 2.0 for newbies)
  const expected = 1 / (1 + Math.pow(10, (opponentElo - teamElo) / 400));
  const actual = result === 'win' ? 1 : result === 'draw' ? 0.5 : 0;
  return Math.round(settings.K_FACTOR * mov * kMultiplier * newbieMul * (actual - expected));
}

export function applyEloUpdate(
  players: any[],
  match: any,
  score1: number,
  score2: number,
  kMultiplier: number = 1,
  now: number = Date.now(),
  settings: EngineSettingsValues = EngineSettings,
): any[] {
  const t1 = [match.team1.p1, match.team1.p2].filter((s: any) => s.kind === 'concrete');
  const t2 = [match.team2.p1, match.team2.p2].filter((s: any) => s.kind === 'concrete');
  if (t1.length !== 2 || t2.length !== 2) return players;

  const t1p = t1.map((s: any) => players.find((p) => p.id === s.playerId)).filter(Boolean);
  const t2p = t2.map((s: any) => players.find((p) => p.id === s.playerId)).filter(Boolean);
  if (t1p.length !== 2 || t2p.length !== 2) return players;

  const t1Elo = (t1p[0].elo + t1p[1].elo) / 2;
  const t2Elo = (t2p[0].elo + t2p[1].elo) / 2;
  const t1Result = score1 > score2 ? 'win' : score1 < score2 ? 'loss' : 'draw';
  const t2Result = score2 > score1 ? 'win' : score2 < score1 ? 'loss' : 'draw';

  // Margin-of-victory — crushing wins produce a larger ELO swing
  const mov = calcMovMultiplier(score1, score2, settings);

  // Asymmetric newbie boost: each team gets its own multiplier based on
  // the "newest" player on the team. If one of the two is a newbie, the whole
  // team gets a boost (they play as a unit). The multiplier decays linearly
  // from max (match 1) to 1.0 (established), and the effective threshold is scaled by
  // kMultiplier so that americano players get the boost for longer.
  const t1NewbieMul = Math.max(
    getPlayerKMultiplier(t1p[0], now, kMultiplier, settings),
    getPlayerKMultiplier(t1p[1], now, kMultiplier, settings),
  );
  const t2NewbieMul = Math.max(
    getPlayerKMultiplier(t2p[0], now, kMultiplier, settings),
    getPlayerKMultiplier(t2p[1], now, kMultiplier, settings),
  );

  const t1Change = calcEloChange(t1Elo, t2Elo, t1Result, mov, kMultiplier, t1NewbieMul, settings);
  const t2Change = calcEloChange(t2Elo, t1Elo, t2Result, mov, kMultiplier, t2NewbieMul, settings);

  // Update ELO + increment matchesPlayed + set lastMatchAt
  return players.map((p) => {
    const isT1 = p.id === t1p[0].id || p.id === t1p[1].id;
    const isT2 = p.id === t2p[0].id || p.id === t2p[1].id;
    if (!isT1 && !isT2) return p;
    const change = isT1 ? t1Change : t2Change;
    return {
      ...p,
      elo: p.elo + change,
      matchesPlayed: (p.matchesPlayed || 0) + 1,
      lastMatchAt: now,
    };
  });
}
